import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../../api/store";
import { CHANNEL_TYPE_ID } from "../../constant/gms-constant";
import { ClientBasicInfoState } from "../../features/gms-proposal-forms/basic-information/basic-information.state";

const numericFields: (keyof ClientBasicInfoState)[] = [
    "noOfLives",
];

export const selectIsBasicInfoComplete = createSelector( (state: RootState) => state.basicInformationSlice.basicInformation, (basicInfo) => {
        return Object.keys(basicInfo).every(key => {

            const field = key as keyof ClientBasicInfoState;

            if (key === "noOfLives") return true;
            
            if ( field === "contactNumber" || field === "companyMobileNo" ) {
                const value = basicInfo[field] as string;
                return value.length === 10;
            }
            
            if (numericFields.includes(field)) {
                return Number(basicInfo[field]) > 0;
            }

            const value = basicInfo[field];

            return (
                typeof value === "string" &&
                value.trim() !== ""
            );
        });
    }
);

const REQUIRES_SUBNAME: number[] = [
    CHANNEL_TYPE_ID.broker,
    CHANNEL_TYPE_ID.generalAgency,
];

export const selectIsChannelTypesSelected = (state: RootState): boolean => {
    const { selectedChannelBox, channelSubName } = state.stepsChannelProposalAsSliceSlice;

    if (!selectedChannelBox || selectedChannelBox.id === CHANNEL_TYPE_ID.select) {
        return false;
    }

    if (REQUIRES_SUBNAME.includes(selectedChannelBox.id)) {
        return !!channelSubName;
    }

    return true;
};

export const selectIsGroupClassificationComplete = (state: RootState): boolean => {
    const { selectedGroupClassificationBox, otherSpecificationInputGC } = state.gcToGEAMoPSlice;

    if (!selectedGroupClassificationBox) return false;

    if (selectedGroupClassificationBox.id === 5) {
        return !!otherSpecificationInputGC && otherSpecificationInputGC.trim() !== "";
    }

    return true;
};

const TYPES_REQUIRING_SUBTYPE = [11, 16];
const OTHERS_ID = 17;
export const selectIsTypeOfGroupComplete = (state: RootState): boolean => {

    const { selectedTypeOfGroupBox, selectedSubType, otherSpecificationInputToG } = state.gcToGEAMoPSlice;

    if (!selectedTypeOfGroupBox || selectedTypeOfGroupBox.id === null) return false;

    if (
        TYPES_REQUIRING_SUBTYPE.includes(selectedTypeOfGroupBox.id) &&
        selectedSubType.length === 0
    ) {
        return false;
    }

    if (selectedTypeOfGroupBox.id === OTHERS_ID) {
        return !!otherSpecificationInputToG && otherSpecificationInputToG.trim() !== "";
    }

    return true;
};

export const selectIsAgeProfileComplete = (state: RootState): boolean => {
    const ageProfile = state.gcToGEAMoPSlice.eligibilityAge;

    return ageProfile.every(item => typeof item.age === "number");
};

export const selectIsFundingArrangementsComplete = (state: RootState): boolean => {
    return state.gcToGEAMoPSlice.selectedFundingArrangements !== null;
};

// STEP 2;
export const selectIsProposalTypeComplete = (state: RootState): boolean => {
    return state.typesProposalProductBasicPlanSlice.selectedProposalType !== null;
};

export const selectIsPrototypeTypeComplete = (state: RootState): boolean => {
    return state.typesProposalProductBasicPlanSlice.selectedPrototypeType !== null;
};

export const selectIsProductTypeComplete = (state: RootState): boolean => {
    return state.typesProposalProductBasicPlanSlice.selectedProductType !== null;
};

export const selectIsBasicPlansComplete = (state: RootState): boolean => {
    return state.typesProposalProductBasicPlanSlice.selectedBasicPlans !== null;
};

export const selectIsCoverageTypeComplete = (state: RootState): boolean => {
    return state.typesProposalProductBasicPlanSlice.selectedCoverageType !== null;
};

export const selectIsAmountLoansComplete = (state: RootState): boolean => {
    return state.amountOfLoansSlice.selectedAmountOfLoans !== null;
};

export const selectIsAmountLoansInputComplete = (state: RootState): boolean => {
    const input = state.amountOfLoansSlice.selectFAmountOfLoans;
    return input !== null || input === 0;
};

export const selectIsLevelRankingComplete = (state: RootState): boolean => {
    const inputs = state.levelRankingSlice.inputs;

    // Return false if any row is incomplete
    for (const row of inputs) {
        // Trim to avoid spaces being counted as valid
        if (!row.label.trim() || row.fAmount === 0 || row.fAmount === null || row.fAmount === undefined) {
            return false;
        }
    }

    return true;
};

export const selectIsUniformCoverageComplete = (state: RootState): boolean => {
    const coverage = state.uniformCoverageSlice.selectedUniformCoverage;
    return !!coverage && coverage.fAmount > 0; // true if fAmount exists and not empty
};

export const selectIsSalaryRankComplete = (state: RootState): boolean => {
    // const rows = state.coverageBySalaryRank.rows;

    // const validCheckedRows = rows.filter(
    //     (row) => row.checked && row.salary > 0 && row.designation.trim() !== ""
    // );

    // return validCheckedRows.length >= 2;

    const rows = state.salaryRankSlice.rows;

    // must have at least 2 rows
    if (rows.length < 2) return false;

    // all rows must be complete
    return rows.every((row) =>
        row.multiplierLabel?.trim() !== "" &&
        row.multiplierValue > 0 &&
        row.salary > 0 &&
        row.designation?.trim() !== "" &&
        row.result > 0
    );
};


export const selectIsAttachableRidersComplete = createSelector(
    [
        (state: RootState) => state.attachableRidersSlice.selectedRiders,
        (state: RootState) => state.attachableRidersSlice.selectedGHIR,
        (state: RootState) => state.attachableRidersSlice.selectedGAMERR9,
        (state: RootState) => state.attachableRidersSlice.selectedGAMERR14,
        (state: RootState) => state.attachableRidersSlice.selectedBMSR,
        (state: RootState) => state.attachableRidersSlice.selectedGDR,
    ],
    (selectedRiders, selectedGHIR, selectedGAMERR, selectedBMSR, selectedGDR) => {

        // no riders selected → still valid
        if (selectedRiders.length === 0) return true;

        return selectedRiders.every((rider) => {
            switch (rider.id) {
                case 7: // GHIR
                    return (
                        !!selectedGHIR &&
                        selectedGHIR.inputs.length > 0 &&
                        selectedGHIR.inputs[0].fAmount > 0
                    );

                case 8: // GAMERR
                    return (
                        !!selectedGAMERR &&
                        selectedGAMERR.inputs.length > 0 &&
                        selectedGAMERR.inputs[0].fAmount > 0
                    );

                case 10: // BB
                    return (
                        !!selectedBMSR &&
                        selectedBMSR.inputs.length > 0 &&
                        selectedBMSR.inputs[0].fAmount > 0
                    );

                case 12: // GDR
                    return (
                        !!selectedGDR &&
                        selectedGDR.inputs.length === 1
                    );

                default:
                    // other riders have no manual input
                    return true;
            }
        });
    }
);

export const selectIsPaymentTermComplete = (state: RootState): boolean => {
    return state.paymentTermLoanMaturityCommissionServicesNotesFilesSlice.selectedPaymentTerm !== null;
};

export const selectIsLoanMaturityComplete = (state: RootState): boolean => {
    return state.paymentTermLoanMaturityCommissionServicesNotesFilesSlice.selectedLoanMaturity !== null;
};

export const selectIsBorrowerAgeComplete = (state: RootState): boolean => {
    return state.ageBracketAndBenefitAmountSlice.borrowerAgeOptions.every(
        (option) => option.value !== null
    );
};